export const flattenConditions = conditions =>
  (conditions || []).flatMap(condition =>
    Array.isArray(condition.conditions)
      ? flattenConditions(condition.conditions)
      : [condition]
  );

export const referencedIds = (rule, attributeKey) =>
  flattenConditions(rule.conditions)
    .filter(
      condition =>
        condition.attribute_key === attributeKey &&
        condition.filter_operator === 'equal_to'
    )
    .flatMap(condition =>
      Array.isArray(condition.values) ? condition.values : [condition.values]
    )
    .filter(value => value !== null && value !== undefined)
    .map(Number);

const firstActionParam = action => {
  const params = Array.isArray(action.action_params)
    ? action.action_params
    : [action.action_params];
  return params[0];
};

const autoAssignActions = rule =>
  rule.actions.filter(action => action.action_name === 'auto_assign');

const currentTeamIds = rule => {
  const assignedTeamIds = rule.actions
    .filter(action => action.action_name === 'assign_team')
    .map(firstActionParam)
    .filter(teamId => !['nil', '0', 0, null, undefined].includes(teamId))
    .map(Number);

  return [...new Set([...referencedIds(rule, 'team_id'), ...assignedTeamIds])];
};

export const teamIdsUsedByRule = rule => {
  const teamIds = autoAssignActions(rule).flatMap(action => {
    const config = firstActionParam(action) || {};
    if (config.pool === 'team') return [Number(config.team_id)];
    if (config.pool === 'current_team') return currentTeamIds(rule);
    return [];
  });

  return [...new Set(teamIds.filter(Boolean))];
};

export const rulesForInbox = (rules, inboxId) =>
  rules.filter(rule =>
    referencedIds(rule, 'inbox_id').includes(Number(inboxId))
  );

export const rulesForTeamInbox = (rules, teamId, inboxId) =>
  rulesForInbox(rules, inboxId).filter(rule =>
    teamIdsUsedByRule(rule).includes(Number(teamId))
  );

export const agentIdsForRules = (rules, inbox, teams, agents) => {
  const inboxAgentIds = new Set(inbox.policy_candidate_agent_ids.map(Number));
  const teamsById = Object.fromEntries(teams.map(team => [team.id, team]));
  const agentsById = Object.fromEntries(agents.map(agent => [agent.id, agent]));
  const candidateIds = new Set();

  const availableAgentIds = (agentIds, availability) => {
    const eligibleIds = agentIds
      .map(Number)
      .filter(id => inboxAgentIds.has(id));
    const onlineIds = eligibleIds.filter(
      id => agentsById[id]?.availability_status === 'online'
    );

    if (availability === 'any') return eligibleIds;
    if (availability === 'online_preferred' && !onlineIds.length) {
      return eligibleIds;
    }

    return onlineIds;
  };

  rules.forEach(rule => {
    autoAssignActions(rule).forEach(action => {
      const config = firstActionParam(action) || {};
      let poolAgentIds = [];

      if (config.pool === 'inbox') {
        poolAgentIds = [...inboxAgentIds];
      } else if (config.pool === 'agents') {
        poolAgentIds = config.agent_ids || [];
      } else if (config.pool === 'team') {
        poolAgentIds = teamsById[Number(config.team_id)]?.member_ids || [];
      } else if (config.pool === 'current_team') {
        poolAgentIds = currentTeamIds(rule).flatMap(
          teamId => teamsById[teamId]?.member_ids || []
        );
      }

      availableAgentIds(
        poolAgentIds,
        config.availability || 'online_only'
      ).forEach(agentId => candidateIds.add(agentId));
    });
  });

  return [...candidateIds];
};
